import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';

const LOGO_URL = 'https://storage.readdy-site.link/project_files/d9e2cd2a-e705-4286-aed3-7c72e1925a95/7a48abf8-a553-49ca-9e61-ca6f4e676f81_compressed_logo_main_rgb.webp';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { totalItems } = useCart();
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Products', href: '/products' },
    { label: 'About', href: '/#about' },
  ];

  const handleAboutClick = (href: string) => (e: React.MouseEvent) => {
    if (href !== '/#about') return;
    if (isHome) {
      e.preventDefault();
      document.getElementById('about')?.scrollIntoView({ behavior: 'auto' });
    }
  };

  const isTransparent = isHome && !scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparent ? 'bg-transparent' : 'bg-background-50/95 backdrop-blur-sm'
      }`}
    >
      <div className="px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center cursor-pointer">
          <img
            src={LOGO_URL}
            alt="ROFF"
            className="h-10 w-auto object-contain"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {navLinks.map(link => (
            <Link
              key={link.href}
              to={link.href}
              onClick={handleAboutClick(link.href)}
              className={`text-sm tracking-[0.15em] transition-colors whitespace-nowrap ${
                isTransparent
                  ? 'text-white/90 hover:text-white'
                  : 'text-foreground-700 hover:text-primary-500'
              }`}
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <Link to="/cart" className="relative cursor-pointer">
            <div className={`w-8 h-8 flex items-center justify-center ${isTransparent ? 'text-white' : 'text-foreground-700'}`}>
              <i className="ri-shopping-bag-line text-lg"></i>
            </div>
            {totalItems > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary-500 text-white text-[10px] rounded-full flex items-center justify-center"
                style={{ fontFamily: "var(--font-heading)" }}>
                {totalItems}
              </span>
            )}
          </Link>
          <button
            className={`md:hidden w-8 h-8 flex items-center justify-center ${isTransparent ? 'text-white' : 'text-foreground-700'}`}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`text-lg ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-50 px-6 py-5 flex flex-col gap-4 border-t border-background-200">
          {navLinks.map(link => (
            <Link
              key={link.href}
              to={link.href}
              className="text-sm text-foreground-700 hover:text-primary-500 tracking-wider transition-colors"
              onClick={(e) => { handleAboutClick(link.href)(e); setMenuOpen(false); }}
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}